import { runOf } from "@/api/pii-vault/use-pii-backfill";
import type { PiiBackfillStateDto } from "@babackapp/private-api-client/generated/schema";
import { type BackfillStatus, summarize } from "./pii-backfill-summary";

export const VERIFY_STATUSES = {
  blocked: "Backfill à terminer",
  pending: "À vérifier",
  running: "En cours",
  paused: "En pause",
  clean: "Conforme",
  mismatched: "Écarts",
} as const;

export type VerifyStatus = keyof typeof VERIFY_STATUSES;

export type ShopVerifySummary = {
  status: VerifyStatus;
  /** Share of documents walked by the verify run, 0 to 100. */
  percent: number;
  /** Documents the vault does not return as they hold them. */
  mismatched: number;
};

/** Where a verify run stands, before looking at what it found. */
const RUN_STATUSES: Record<
  BackfillStatus,
  Exclude<VerifyStatus, "mismatched">
> = {
  never: "pending",
  outdated: "pending",
  running: "running",
  paused: "paused",
  partial: "paused",
  done: "clean",
};

/**
 * A shop is verified once a verify run finished on it without any mismatch.
 * It waits for its backfill first: only recorded documents can be checked.
 */
export const summarizeVerify = (
  states: PiiBackfillStateDto[],
): ShopVerifySummary => {
  if (summarize(runOf(states, "record")).status !== "done") {
    return { status: "blocked", percent: 0, mismatched: 0 };
  }
  const run = runOf(states, "verify");
  const { status, percent } = summarize(run);
  const mismatched = run.reduce((sum, state) => sum + state.mismatched, 0);
  return {
    status:
      status === "done" && mismatched > 0 ? "mismatched" : RUN_STATUSES[status],
    percent,
    mismatched,
  };
};
